import React, { useState } from "react";
import { ArrowRight, AlertTriangle, Gavel, Loader2, UserCheck, CalendarClock } from "lucide-react";
import { OS_DECISION_LABEL, osDaysLeft } from "./osShared";

export const DueBadge = ({ dueDate, status }) => {
  if (!dueDate || status === "DECIDED" || status === "ARCHIVED") return null;
  const d = osDaysLeft(dueDate);
  const cls = d < 0 ? "bg-red-100 text-red-800 border-red-200"
    : d <= 14 ? "bg-red-50 text-red-700 border-red-200"
    : d <= 28 ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-zinc-100 text-zinc-600 border-zinc-200";
  return (
    <span data-testid="os-due-badge" className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${cls}`}>
      <CalendarClock className="w-3 h-3" />
      {d < 0 ? `Frist passerad ${-d} d` : `${d} d kvar (PBL)`}
    </span>
  );
};

export const TransitionBar = ({ transitions, onTransition, busy }) => (
  <div className="mt-4 space-y-2">
    <div className="flex flex-wrap gap-2">
      {transitions.map((t) => (
        <button key={t.to} disabled={busy || !t.ok} onClick={() => onTransition(t)} data-testid={`os-transition-${t.to}`}
          title={t.ok ? t.label : t.failures.map((f) => f.message).join(", ")}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-sm">
          {t.requiresDecision ? <Gavel className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />} {t.label}
        </button>
      ))}
    </div>
    {transitions.filter((t) => !t.ok).map((t) => (
      <div key={t.to} data-testid={`os-guard-hint-${t.to}`} className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span><b>{t.label}</b> kräver: {t.failures.map((f) => f.message).join("; ")}</span>
      </div>
    ))}
  </div>
);

export const DecisionModal = ({ open, onClose, onSubmit, busy }) => {
  const [outcome, setOutcome] = useState("BEVILJAT");
  const [motivation, setMotivation] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-sm" data-testid="os-decision-modal">
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-sm p-6">
        <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Gavel className="w-4 h-4" /> Fatta beslut
        </h3>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Utfall</label>
        <div className="mt-1.5 flex gap-2">
          {Object.entries(OS_DECISION_LABEL).map(([k, v]) => (
            <button key={k} onClick={() => setOutcome(k)} data-testid={`os-decision-outcome-${k}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm border ${outcome === k
                ? (k === "BEVILJAT" ? "bg-emerald-700 text-white border-emerald-700" : "bg-red-700 text-white border-red-700")
                : "bg-white text-zinc-600 border-zinc-300"}`}>
              {v}
            </button>
          ))}
        </div>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Motivering</label>
        <textarea value={motivation} onChange={(e) => setMotivation(e.target.value)} rows={4} data-testid="os-decision-motivation"
          placeholder="Motivera beslutet (krävs)…"
          className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700" />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm">Avbryt</button>
          <button disabled={!motivation.trim() || busy} onClick={() => onSubmit({ outcome, motivation })} data-testid="os-decision-submit"
            className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm disabled:opacity-50 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Fatta beslut
          </button>
        </div>
      </div>
    </div>
  );
};

export const DecisionCard = ({ decision }) => {
  if (!decision) return null;
  const granted = decision.outcome === "BEVILJAT";
  return (
    <div data-testid="os-decision-card" className={`mt-5 border rounded-sm p-5 ${granted ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
      <h3 className={`text-sm font-semibold uppercase tracking-wider flex items-center gap-2 ${granted ? "text-emerald-800" : "text-red-800"}`}>
        <Gavel className="w-4 h-4" /> Beslut: {OS_DECISION_LABEL[decision.outcome] || decision.outcome}
      </h3>
      <p className="mt-2 text-sm text-zinc-700 whitespace-pre-wrap">{decision.motivation}</p>
      <p className="mt-2 text-[11px] text-zinc-500">Beslutat {(decision.decidedAt || "").slice(0, 16).replace("T", " ")}</p>
    </div>
  );
};

export const AssigneePicker = ({ caseData, users, onAssign, canWrite }) => {
  const assignee = users.find((u) => u.id === caseData.assigneeId);
  if (!canWrite) {
    return assignee ? (
      <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600" data-testid="os-assignee-readonly">
        <UserCheck className="w-3.5 h-3.5" /> {assignee.email}
      </span>
    ) : null;
  }
  return (
    <div className="inline-flex items-center gap-2" data-testid="os-assignee-picker">
      <UserCheck className="w-4 h-4 text-zinc-500" />
      <select value={caseData.assigneeId || ""} onChange={(e) => onAssign(e.target.value || null)} data-testid="os-assignee-select"
        className="px-2 py-1.5 text-xs border border-zinc-300 rounded-sm outline-none focus:border-blue-700">
        <option value="">Ej tilldelat</option>
        {users.filter((u) => ["ADMIN", "HANDLAGGARE"].includes(u.role)).map((u) => (
          <option key={u.id} value={u.id}>{`${u.email} (${u.role})`}</option>
        ))}
      </select>
    </div>
  );
};
